import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { AdminTone, adminToneClasses } from "./adminStyles";

interface AdminPanelProps {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
}

export function AdminPanel({
  title,
  description,
  actions,
  children,
  className,
  contentClassName,
  headerClassName,
}: AdminPanelProps) {
  return (
    <Card className={cn("overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm", className)}>
      {(title || description || actions) && (
        <CardHeader
          className={cn(
            "flex flex-col gap-3 border-b border-gray-100 bg-white px-5 py-4 sm:flex-row sm:items-start sm:justify-between",
            headerClassName,
          )}
        >
          <div className="space-y-1">
            {title ? <CardTitle className="text-base font-semibold tracking-normal text-gray-800">{title}</CardTitle> : null}
            {description ? <CardDescription className="max-w-3xl text-sm leading-5 text-gray-500">{description}</CardDescription> : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </CardHeader>
      )}
      <CardContent className={cn("p-5", contentClassName)}>{children}</CardContent>
    </Card>
  );
}

interface AdminFilterBarProps {
  children: React.ReactNode;
  className?: string;
}

export function AdminFilterBar({ children, className }: AdminFilterBarProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface AdminMetricCardProps {
  label: string;
  value: string | number;
  description?: string;
  trend?: string;
  icon: LucideIcon;
  tone?: AdminTone;
  href?: string;
}

export function AdminMetricCard({
  label,
  value,
  description,
  trend,
  icon: Icon,
  tone = "neutral",
  href,
}: AdminMetricCardProps) {
  const content = (
    <div
      className={cn(
        "group flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all",
        href ? "hover:-translate-y-0.5 hover:border-whatsapp/30 hover:shadow-md" : "",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="text-2xl font-semibold tracking-tight text-gray-800">{value}</p>
        </div>
        <div className={cn("flex h-10 w-10 items-center justify-center rounded-full", adminToneClasses[tone])}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      {(description || trend) && (
        <div className="mt-4 space-y-1">
          {description ? <p className="text-sm text-gray-500">{description}</p> : null}
          {trend ? <p className="text-xs font-medium text-whatsapp/80">{trend}</p> : null}
        </div>
      )}
    </div>
  );

  if (!href) {
    return content;
  }

  return (
    <Link to={href} className="block h-full">
      {content}
    </Link>
  );
}

interface AdminEmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: React.ReactNode;
  className?: string;
}

export function AdminEmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: AdminEmptyStateProps) {
  return (
    <div
      className={cn(
        "flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center",
        className,
      )}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-whatsapp-light text-whatsapp">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">{description}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}
